import React, { useEffect, useState, useCallback } from 'react';
import { apiService } from '../services/apiWrapper';
import { useLanguage } from '../i18n/LanguageContext';
import './MaintenanceTaskList.css';

function MaintenanceTaskList() {
  const { t } = useLanguage();
  const [tasks, setTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({
    status: '',
    priority: '',
    search: ''
  });

  const [showModal, setShowModal] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium',
    status: 'pending',
    device_id: ''
  });

  const [currentUser, setCurrentUser] = useState(null);

  const loadTasks = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.maintenance.getTasks();
      const data = response?.data;
      if (Array.isArray(data)) {
        setTasks(data);
      } else if (data && Array.isArray(data.tasks)) {
        setTasks(data.tasks);
      } else if (data && Array.isArray(data.data)) {
        setTasks(data.data);
      } else if (data && typeof data === 'string' && (data.trim().toLowerCase().startsWith('<!') || data.includes('<!doctype'))) {
        console.warn('API върна HTML вместо JSON – backend не е достъпен или REACT_APP_API_URL е грешен.');
        setTasks([]);
      } else {
        const isHtml = typeof data === 'string' && (data.trim().toLowerCase().startsWith('<!') || data.includes('<!doctype'));
        if (isHtml) {
          console.warn('API върна HTML вместо JSON – проверете дали backend сървърът работи и REACT_APP_API_URL.');
        } else {
          console.warn('Неочакван формат на данните:', typeof data, Array.isArray(data) ? 'масив' : '');
        }
        setTasks([]);
      }
    } catch (error) {
      console.error('Грешка при зареждане на задачи:', error?.message || error);
      setTasks([]);
      const msg = error?.message || t('maintenance.errorLoading');
      if (msg.includes('HTML') || msg.includes('REACT_APP_API')) {
        alert(msg + '\n\nMake sure the backend server is running and REACT_APP_API_URL points to the correct API URL.');
      } else {
        alert(t('maintenance.errorLoading'));
      }
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    // Зареди текущия потребител от localStorage
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        setCurrentUser(JSON.parse(userStr));
      } catch (e) {
        console.error('Грешка при парсване на потребител:', e);
      }
    }

    loadTasks();
  }, [loadTasks]);

  const applyFilters = useCallback(() => {
    // Проверка дали tasks е масив
    if (!Array.isArray(tasks)) {
      setFilteredTasks([]);
      return;
    }

    let filtered = [...tasks];

    if (filter.status) {
      filtered = filtered.filter(t => t && t.status === filter.status);
    }

    if (filter.priority) {
      filtered = filtered.filter(t => t && t.priority === filter.priority);
    }

    if (filter.search) {
      const searchLower = filter.search.toLowerCase();
      filtered = filtered.filter(t =>
        t && (
          (t.title && t.title.toLowerCase().includes(searchLower)) ||
          (t.description && t.description.toLowerCase().includes(searchLower)) ||
          (t.assigned_to_name && t.assigned_to_name.toLowerCase().includes(searchLower))
        )
      );
    }

    setFilteredTasks(filtered);
  }, [filter, tasks]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const handleFilterChange = (key, value) => {
    setFilter(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilter({ status: '', priority: '', search: '' });
  };

  const openCreateModal = () => {
    setEditTask(null);
    setFormData({
      title: '',
      description: '',
      priority: 'medium',
      status: 'pending',
      device_id: ''
    });
    setShowModal(true);
  };

  const openEditModal = (task) => {
    setEditTask(task);
    setFormData({
      title: task.title,
      description: task.description,
      priority: task.priority,
      status: task.status,
      device_id: task.device_id || ''
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditTask(null);
  };

  const handleFormChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editTask) {
        await apiService.maintenance.updateTask(editTask.id, formData);
        alert(t('maintenance.successUpdate'));
      } else {
        await apiService.maintenance.createTask(formData);
        alert(t('maintenance.successCreate'));
      }

      closeModal();
      loadTasks();
    } catch (error) {
      console.error('Грешка при запис:', error);
      alert(t('maintenance.errorSaving'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('maintenance.confirmDelete'))) {
      return;
    }

    try {
      await apiService.maintenance.delete(id);
      alert(t('maintenance.successDelete'));
      loadTasks();
    } catch (error) {
      console.error('Грешка при изтриване:', error);
      alert(t('maintenance.errorDeleting'));
    }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      await apiService.maintenance.updateTask(taskId, { status: newStatus });
      alert(`${t('maintenance.successStatusChange')}: ${getStatusLabel(newStatus)}`);
      loadTasks();
    } catch (error) {
      console.error('Грешка при промяна на статус:', error);
      alert(t('maintenance.errorStatusChange'));
    }
  };

  const getPriorityBadge = (priority) => {
    const badges = {
      low: { class: 'badge-low', icon: '🔵', text: t('maintenance.low') },
      medium: { class: 'badge-medium', icon: '🟡', text: t('maintenance.medium') },
      high: { class: 'badge-high', icon: '🟠', text: t('maintenance.high') },
      critical: { class: 'badge-critical', icon: '🔴', text: t('maintenance.critical') }
    };
    const badge = badges[priority] || badges.medium;
    return (
      <span className={`badge ${badge.class}`}>
        {badge.icon} {badge.text}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { class: 'badge-pending', icon: '⏳', text: t('maintenance.pending') },
      in_progress: { class: 'badge-progress', icon: '🔄', text: t('maintenance.inProgress') },
      completed: { class: 'badge-completed', icon: '✅', text: t('maintenance.completed') },
      cancelled: { class: 'badge-cancelled', icon: '❌', text: t('maintenance.cancelled') }
    };
    const badge = badges[status] || badges.pending;
    return (
      <span className={`badge ${badge.class}`}>
        {badge.icon} {badge.text}
      </span>
    );
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: t('maintenance.pending'),
      in_progress: t('maintenance.inProgress'),
      completed: t('maintenance.completed'),
      cancelled: t('maintenance.cancelled')
    };
    return labels[status] || status;
  };

  if (loading) {
    return (
      <div className="tasks-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>{t('maintenance.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="tasks-container">
      <div className="tasks-header">
        <h2>🔧 {t('maintenance.title')}</h2>
        {currentUser && currentUser.role === 'admin' && (
          <button className="btn btn-primary" onClick={openCreateModal}>
            ➕ {t('maintenance.newTask')}
          </button>
        )}
      </div>

      {/* Филтри */}
      <div className="filters-bar">
        <div className="filter-group">
          <label>🔍 {t('common.search')}:</label>
          <input
            type="text"
            placeholder={t('common.search')}
            value={filter.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            className="filter-input"
          />
        </div>

        <div className="filter-group">
          <label>📊 {t('maintenance.status')}:</label>
          <select
            value={filter.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            className="filter-select"
          >
            <option value="">{t('common.all')}</option>
            <option value="pending">{t('maintenance.pending')}</option>
            <option value="in_progress">{t('maintenance.inProgress')}</option>
            <option value="completed">{t('maintenance.completed')}</option>
            <option value="cancelled">{t('maintenance.cancelled')}</option>
          </select>
        </div>

        <div className="filter-group">
          <label>⚠️ {t('maintenance.priority')}:</label>
          <select
            value={filter.priority}
            onChange={(e) => handleFilterChange('priority', e.target.value)}
            className="filter-select"
          >
            <option value="">{t('common.all')}</option>
            <option value="low">{t('maintenance.low')}</option>
            <option value="medium">{t('maintenance.medium')}</option>
            <option value="high">{t('maintenance.high')}</option>
            <option value="critical">{t('maintenance.critical')}</option>
          </select>
        </div>

        <button className="btn btn-secondary" onClick={clearFilters}>
          🔄 {t('common.clear')}
        </button>
      </div>

      {/* Таблица със задачи */}
      <div className="card">
        <p className="results-count">
          {t('common.showing')}: {filteredTasks.length} {t('common.of')} {tasks.length} {t('maintenance.title').toLowerCase()}
        </p>

        {filteredTasks.length > 0 ? (
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>{t('maintenance.taskName')}</th>
                  <th>{t('maintenance.priority')}</th>
                  <th>{t('maintenance.status')}</th>
                  <th>{t('maintenance.device')}</th>
                  <th>{t('maintenance.assignedTo')}</th>
                  <th>{t('common.created')}</th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredTasks.map(task => (
                  <tr key={task.id}>
                    <td>{task.id}</td>
                    <td>
                      <div className="task-title-cell">
                        <strong>{task.title}</strong>
                        {task.description && (
                          <p className="task-description">{task.description}</p>
                        )}
                      </div>
                    </td>
                    <td>{getPriorityBadge(task.priority)}</td>
                    <td>
                      <div className="status-dropdown">
                        {getStatusBadge(task.status)}
                        {currentUser && (currentUser.role === 'admin' || task.assigned_to === currentUser.id) && (
                          <select
                            className="status-select"
                            value={task.status}
                            onChange={(e) => handleStatusChange(task.id, e.target.value)}
                          >
                            <option value="pending">⏳ {t('maintenance.pending')}</option>
                            <option value="in_progress">🔄 {t('maintenance.inProgress')}</option>
                            <option value="completed">✅ {t('maintenance.completed')}</option>
                            <option value="cancelled">❌ {t('maintenance.cancelled')}</option>
                          </select>
                        )}
                      </div>
                    </td>
                    <td>
                      {task.device_name ? (
                        <>📱 {task.device_name}</>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td>
                      {task.assigned_to_name ? (
                        <>👤 {task.assigned_to_name}</>
                      ) : (
                        <span className="text-muted">{t('common.unassigned')}</span>
                      )}
                    </td>
                    <td>{new Date(task.created_at).toLocaleDateString('bg-BG')}</td>
                    <td className="actions-cell">
                      {currentUser && currentUser.role === 'admin' && (
                        <>
                          <button
                            className="btn-icon btn-edit"
                            onClick={() => openEditModal(task)}
                            title={t('common.edit')}
                          >
                            ✏️
                          </button>
                          <button
                            className="btn-icon btn-delete"
                            onClick={() => handleDelete(task.id)}
                            title={t('common.delete')}
                          >
                            🗑️
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="no-data">{t('maintenance.noResults')}</p>
        )}
      </div>

      {/* Modal за създаване/редактиране */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {editTask ? `✏️ ${t('common.edit')} ${t('maintenance.title').toLowerCase()}` : `➕ ${t('maintenance.newTask')}`}
              </h3>
              <button className="modal-close" onClick={closeModal}>✖</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>{t('common.title')} *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleFormChange}
                  required
                  placeholder={t('maintenance.taskName')}
                />
              </div>

              <div className="form-group">
                <label>{t('maintenance.description')}</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleFormChange}
                  rows="4"
                  placeholder={t('maintenance.description')}
                />
              </div>

              <div className="form-group">
                <label>{t('maintenance.priority')} *</label>
                <select
                  name="priority"
                  value={formData.priority}
                  onChange={handleFormChange}
                  required
                >
                  <option value="low">🔵 {t('maintenance.low')}</option>
                  <option value="medium">🟡 {t('maintenance.medium')}</option>
                  <option value="high">🟠 {t('maintenance.high')}</option>
                  <option value="critical">🔴 {t('maintenance.critical')}</option>
                </select>
              </div>

              <div className="form-group">
                <label>{t('maintenance.status')} *</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleFormChange}
                  required
                >
                  <option value="pending">⏳ {t('maintenance.pending')}</option>
                  <option value="in_progress">🔄 {t('maintenance.inProgress')}</option>
                  <option value="completed">✅ {t('maintenance.completed')}</option>
                  <option value="cancelled">❌ {t('maintenance.cancelled')}</option>
                </select>
              </div>

              <div className="form-group">
                <label>{t('maintenance.device')} ID ({t('common.optional')})</label>
                <input
                  type="number"
                  name="device_id"
                  value={formData.device_id}
                  onChange={handleFormChange}
                  placeholder="e.g. 1"
                />
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>
                  {t('common.cancel')}
                </button>
                <button type="submit" className="btn btn-primary">
                  {editTask ? t('common.saveChanges') : t('maintenance.addTask')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default MaintenanceTaskList;
